import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { showToast } from '../../Components/utils/ShowToast';
import { useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import axios from 'axios';
import { LoginContext } from '../../Contexts/LoginContext';
import { useCart } from '../../Contexts/CartContext';
import { useFloatingChat } from '../../Contexts/FloatingChatContext';
import Dropdown from '../../Components/utils/Dropdown';
import countries from '../../data/countries.json';
import { formatCurrencyDual, formatCurrencyDualText, calculateDistance, states, feeCategoryOf, feePercentFrom, listingPoint } from '../../data/constants';
import Loader from '../../Components/utils/Loader';
import { FaMapMarkerAlt, FaCalendarAlt, FaUser, FaCreditCard, FaComment, FaTruck, FaStore, FaShoppingCart } from 'react-icons/fa';
import { dateKeyLocal } from '../../utils/orderFulfillmentDate';

/**
 * Parse time string like "2 Days", "1 Hour", "0.5 Days" into { value, unit }
 */
const parseTimeString = (str) => {
  if (!str || typeof str !== 'string') return null;
  const match = str.trim().match(/^(\d+(?:\.\d+)?)\s*(hour|hours|day|days|minute|minutes)$/i);
  if (!match) return null;
  return { value: parseFloat(match[1]), unit: match[2].toLowerCase() };
};

/**
 * Earliest date the buyer may pick, in the buyer's own calendar.
 * - Days: today + N (2 Days → not today or tomorrow)
 * - Hours / minutes: the day the lead time runs out (1 Hour at 23:30 → tomorrow)
 * Mirrors backend utils/leadTime.js — keep in step.
 */
const getMinSelectableDate = (timeStr) => {
  const parsed = parseTimeString(timeStr);
  if (!parsed) return null;

  const ready = new Date();
  if (parsed.unit.startsWith('day')) {
    ready.setDate(ready.getDate() + Math.ceil(parsed.value));
  } else {
    const unitMs = parsed.unit.startsWith('hour') ? 60 * 60 * 1000 : 60 * 1000;
    ready.setTime(ready.getTime() + parsed.value * unitMs);
  }
  return dateKeyLocal(ready);
};

const CheckoutPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { user, feesObj, currency, exchangeRate, fetchUserData } = useContext(LoginContext);
  const { removeCartLinesByIds, clearCart } = useCart();
  const { openFloatingChat } = useFloatingChat();
  const [searchParams] = useSearchParams();
  const messageId = searchParams.get('c');

  const {
    orderItems: initialOrderItems = [],
    aggregateSummary: initialAggregateSummary = { subtotal: 0, serviceFee: 0, deliveryFee: 0, tax: 0, total: 0 },
    pickupInfo,
    sellerLocation,
    fromCart: navigationFromCart = false,
  } = location.state || {};

  const buyerLat = user?.buyerAddress?.location?.lat || user?.location?.lat;
  const buyerLon = user?.buyerAddress?.location?.lon || user?.location?.lon;

  /**
   * Optimistic figures only — /orders/quote replaces them with the server's
   * before anything is charged. The cart endpoint already resolves each line's
   * band; `feesObj.buyer` covers lines it could not.
   */
  const buyerFeeOf = (item) => Number(item?.buyerFeePercent ?? feesObj.buyer) || 0;

  const buyerFeeForListing = (listing, isB2B) => {
    const category = feeCategoryOf({ isB2B, listingType: listing?.listingType });
    return feePercentFrom(feesObj.buyerMatrix, category) || Number(feesObj.buyer) || 0;
  };

  const [formData, setFormData] = useState({
    country: null,
    state: null,
    city: '',
    address: '',
    postalCode: '',
    unitNumber: '',
    fullName: '',
    email: '',
    phone: '',
    note: '',
    paymentMethod: 'Stripe',
    requestedDate: '',
  });
  const [placeOrderLoading, setPlaceOrderLoading] = useState(false);
  const [saveAsDefaultAddress, setSaveAsDefaultAddress] = useState(false);
  const [taxRate, setTaxRate] = useState(0);
  const [orderItems, setOrderItems] = useState(initialOrderItems);
  const [updatedAggregateSummary, setUpdatedAggregateSummary] = useState(initialAggregateSummary);
  const [cartSynced, setCartSynced] = useState(false);
  const [checkoutCartLoading, setCheckoutCartLoading] = useState(false);
  const [minSelectableDate, setMinSelectableDate] = useState('');
  const [quotedMinTimeStr, setQuotedMinTimeStr] = useState('');
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [sellerUserId, setSellerUserId] = useState(null);
  const [checkoutMeta, setCheckoutMeta] = useState({ minTimeStr: '', deliveryType: '' });
  const [dateValidationError, setDateValidationError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [derivedPickupInfo, setDerivedPickupInfo] = useState(null);
  const [derivedSellerLocation, setDerivedSellerLocation] = useState(null);
  const [requirementAnswers, setRequirementAnswers] = useState([]);
  const [isServiceOrder, setIsServiceOrder] = useState(false);
  const [serviceMode, setServiceMode] = useState('');
  const [sellingScope, setSellingScope] = useState('local');
  const [buyerBalance, setBuyerBalance] = useState(null);
  /** When true, apply min(balance, order total) from platform credits; remainder is charged to card. */
  const [applyPlatformCredits, setApplyPlatformCredits] = useState(true);
  const effectivePickupInfo = pickupInfo || derivedPickupInfo;
  const effectiveSellerLocation = sellerLocation || derivedSellerLocation;

  const todayStr = dateKeyLocal(new Date());

  // The quote covers every item in the basket; the per-listing lookups below only see the first.
  const quotedMinDate = getMinSelectableDate(quotedMinTimeStr) || '';
  const quoteIsStricter = quotedMinDate > minSelectableDate;
  const earliestDate = quoteIsStricter ? quotedMinDate : minSelectableDate;
  const minTimeLabel = quoteIsStricter ? quotedMinTimeStr : checkoutMeta.minTimeStr;

  // Only show pickup distance for local listings where buyer and pickup are in the same country.
  // This prevents incorrect large distances (e.g. 10,674 km) when a seller's profile geo-location
  // doesn't match the actual pickup address (which may be in a different location).
  const pickupCountry = effectivePickupInfo?.country || '';
  const buyerCountryForKm = user?.buyerAddress?.country?.name || '';
  const isLocalAndSameCountry =
    sellingScope === 'local' &&
    (!pickupCountry || !buyerCountryForKm || pickupCountry === buyerCountryForKm);

  const kmAway =
    (isLocalAndSameCountry && effectivePickupInfo && effectiveSellerLocation?.lat && buyerLat)
      ? calculateDistance(buyerLat, buyerLon, effectiveSellerLocation.lat, effectiveSellerLocation.lon)
      : null;

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        country: user.buyerAddress?.country ? { value: user.buyerAddress.country.code, label: user.buyerAddress.country.name } : null,
        state: user.buyerAddress?.state ? { value: user.buyerAddress.state.name, label: user.buyerAddress.state.name } : null,
        city: user.buyerAddress?.city || '',
        address: user.buyerAddress?.address || '',
        postalCode: user.buyerAddress?.zipCode || '',
        unitNumber: '',
        fullName: user.fullName || user.sellerId?.fullName || '',
        email: user.email || '',
        phone: user.phone || '',
      }));
    }
  }, [user]);

  // When user opens checkout without state (e.g. from Account Credits), fetch cart from API and build orderItems
  useEffect(() => {
    if (messageId || initialOrderItems.length > 0 || !user?._id || !feesObj?.loaded) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    setCheckoutCartLoading(true);
    axios
      .get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/cart`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const cartItemsFromApi = res.data.cartItems || [];
        const taxRateForCart = typeof res.data.taxRate === 'number' ? res.data.taxRate : 0;
        if (cartItemsFromApi.length === 0) {
          setOrderItems([]);
          setCheckoutCartLoading(false);
          return;
        }
        const orderItemsFromCart = cartItemsFromApi.map((item) => {
          const price = Number(item.price) || 0;
          const quantity = Number(item.quantity) || 1;
          const addedExtra = Number(item.addedServicesTotal) || 0;
          const itemSubtotal = price * quantity + addedExtra;
          const itemDeliveryFee = item.deliveryType === 'delivery' ? (Number(item.deliveryFee) || 0) : 0;
          const itemServiceFee = (itemSubtotal + itemDeliveryFee) * (buyerFeeOf(item) / 100);
          const itemTax = (itemSubtotal + itemDeliveryFee + itemServiceFee) * (taxRateForCart / 100);
          const itemTotal = itemSubtotal + itemDeliveryFee + itemServiceFee + itemTax;
          let selectedOptions = item.selectedOptions || {};
          if (item.pricingType === 'variable' && selectedOptions.variablePricing) {
            const variables = (selectedOptions.variablePricing.variables || []).map((varItem) => {
              if (varItem?.selectedOption?.name === undefined) return null;
              return varItem;
            });
            selectedOptions = {
              variablePricing: {
                variables: variables.filter((v) => v !== null),
                basePrice: selectedOptions.variablePricing.basePrice,
              },
            };
          }
          return {
            cartItemId: item._id,
            productId: item.productId,
            isB2B: Boolean(item.isB2B),
            quantity,
            selectedOptions,
            price,
            deliveryType: item.deliveryType,
            name: item.name || '',
            thumbnail: item.thumbnail || '',
            pricingType: item.pricingType || 'fixed',
            addedServices: item.addedServices || [],
            addedServicesTotal: addedExtra,
            summary: {
              price,
              quantity,
              subtotal: itemSubtotal,
              deliveryFee: itemDeliveryFee,
              serviceFee: itemServiceFee,
              tax: itemTax,
              total: itemTotal,
            },
            orderType: 'single',
            amountToPay: itemTotal,
          };
        });
        const aggregate = {
          subtotal: orderItemsFromCart.reduce((acc, item) => acc + item.summary.subtotal, 0),
          deliveryFee: orderItemsFromCart.reduce((acc, item) => acc + item.summary.deliveryFee, 0),
          serviceFee: orderItemsFromCart.reduce((acc, item) => acc + item.summary.serviceFee, 0),
          tax: orderItemsFromCart.reduce((acc, item) => acc + item.summary.tax, 0),
          total: orderItemsFromCart.reduce((acc, item) => acc + item.summary.total, 0),
        };
        setOrderItems(orderItemsFromCart);
        setUpdatedAggregateSummary(aggregate);
        setTaxRate(taxRateForCart);
        setCartSynced(true);
      })
      .catch(() => {
        setOrderItems([]);
      })
      .finally(() => {
        setCheckoutCartLoading(false);
      });
  }, [messageId, initialOrderItems.length, user?._id, feesObj?.loaded]);

  // When orderItems were loaded from cart, fetch first product for minDate, pickup info, etc.
  useEffect(() => {
    if (messageId || !cartSynced || !orderItems?.length) return;
    const firstProductId = orderItems[0].productId;
    if (orderItems[0].isB2B) {
      axios
        .get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/b2b/listings/${firstProductId}`)
        .then((res) => {
          const listing = res.data.listing;
          const leadDays = (Number(listing?.productionTimeDays) || 0) + (Number(listing?.shippingTimeDays) || 0);
          const timeStr = `${Math.max(1, leadDays)} Days`;
          const minDate = getMinSelectableDate(timeStr);
          setCheckoutMeta({ minTimeStr: timeStr, deliveryType: orderItems[0].deliveryType || 'delivery' });
          setMinSelectableDate(minDate || '');
          setIsServiceOrder(listing?.listingType === 'service');
          setServiceMode('');
          setUnavailableDates([]);
          setSellerUserId(listing?.b2bSellerId?.userId || null);
          setDerivedPickupInfo(null);
          setDerivedSellerLocation(null);
        })
        .catch(() => setMinSelectableDate(getMinSelectableDate('1 Days') || ''));
      return;
    }

    axios
      .get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/products/product/${firstProductId}`)
      .then((res) => {
        const p = res.data.product;
        const isService = p?.listingType === 'service';
        const sMode = isService ? (p.serviceMode || '') : '';
        setIsServiceOrder(!!isService);
        setServiceMode(sMode);
        setSellingScope(p?.sellingScope || 'local');
        const deliveryType = orderItems[0].deliveryType;
        const isFixed = p?.pricingType === 'fixed';
        const pkgIndex = orderItems[0]?.selectedOptions?.fixedPricing?.packageIndex ?? 0;
        const opts = isFixed ? p?.packages?.[pkgIndex]?.options : p?.variablePricing;
        const timeStr = opts?.[deliveryType === 'delivery' ? 'timeToDeliver' : 'timeframe'] || opts?.timeToDeliver || '1 Days';
        const minDate = getMinSelectableDate(timeStr);
        setCheckoutMeta({ minTimeStr: timeStr, deliveryType });
        setMinSelectableDate(minDate || '');
        if (deliveryType === 'pickup' && p?.pickupLocation) {
          setDerivedPickupInfo({
            address: p.pickupLocation.address || '',
            city: p.pickupLocation.city || '',
            state: p.pickupLocation.state?.name || '',
            country: p.pickupLocation.country?.name || '',
            zipCode: p.pickupLocation.zipCode || '',
          });
        }
        const seller = p?.sellerId;
        setDerivedSellerLocation(listingPoint(p) || null);
        const sellerId = seller?._id || seller?.id;
        if (sellerId) {
          setSellerUserId(seller?.userId?._id || seller?.userId);
          axios
            .get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/sellers/seller/${sellerId}/unavailable-dates`)
            .then((r) => setUnavailableDates(r.data.unavailableDates || []))
            .catch(() => setUnavailableDates([]));
        }
      })
      .catch(() => setMinSelectableDate(getMinSelectableDate('1 Days') || ''));
  }, [messageId, cartSynced, orderItems]);

  const totalToPay = (orderItems.length > 0 && orderItems[0]?.orderType === 'deposit')
    ? (orderItems[0]?.amountToPay ?? 0)
    : (updatedAggregateSummary?.total ?? 0);
  const balanceNum = typeof buyerBalance === 'number' ? buyerBalance : 0;
  const creditToApply =
    applyPlatformCredits && balanceNum > 0 && totalToPay > 0
      ? Math.min(balanceNum, totalToPay)
      : 0;
  const cardAmountDue = Math.max(0, totalToPay - creditToApply);
  const paysWithCreditsOnly = creditToApply >= totalToPay && totalToPay > 0;

  useEffect(() => {
    if (!user?._id || (orderItems.length === 0 && !messageId)) return;
    const token = localStorage.getItem('token');
    axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/buyer/balance`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setBuyerBalance(res.data?.balance ?? 0))
      .catch(() => setBuyerBalance(0));
  }, [user?._id, orderItems.length, messageId]);

  useEffect(() => {
    if (messageId) {
      setPlaceOrderLoading(true);
      axios
        .get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/chats/offer/details/${messageId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        })
        .then((response) => {
          const offer = response.data.offer;
          const isService = offer.productId?.listingType === 'service';
          const sMode = isService ? (offer.productId?.serviceMode || '') : '';
          setIsServiceOrder(isService);
          setServiceMode(sMode);
          const product = offer.productId;
          setSellingScope(product?.sellingScope || 'local');
          const quantity = offer.quantity;
          const addedServicesTotal = (offer.addedServices || []).reduce((sum, svc) => sum + (Number(svc?.price) || 0), 0);
          const subtotal = (offer.price * quantity) + addedServicesTotal;
          const deliveryFee = offer.deliveryType === 'delivery' ? Number(offer.deliveryFee) || 0 : 0;
          const serviceFee = (subtotal + deliveryFee) * (buyerFeeForListing(product, false) / 100);
          const tax = (subtotal + deliveryFee + serviceFee) * (taxRate / 100);
          const total = subtotal + deliveryFee + serviceFee + tax;
          const depositAmount = offer.orderType === 'deposit' ? total * (offer.depositPercentage / 100) : undefined;
          const amountToPay = offer.orderType === 'deposit' ? depositAmount : total;

          const timeStr = `${offer.deliveryTime.number} ${offer.deliveryTime.unit}`;
          const minDate = getMinSelectableDate(timeStr);

          setCheckoutMeta({ minTimeStr: timeStr, deliveryType: offer.deliveryType });
          setMinSelectableDate(minDate || '');

          if (product?.sellerId?._id || product?.sellerId) {
            const sid = product.sellerId?._id || product.sellerId;
            setSellerUserId(product.sellerId?.userId?._id || product.sellerId?.userId);
            axios
              .get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/sellers/seller/${sid}/unavailable-dates`)
              .then((r) => setUnavailableDates(r.data.unavailableDates || []))
              .catch(() => setUnavailableDates([]));
          }

          const orderItem = {
            productId: product._id,
            quantity,
            selectedOptions: {},
            price: offer.price,
            deliveryType: offer.deliveryType,
            timeToDeliveryOrPickup: timeStr,
            name: offer.title,
            thumbnail: offer.productImage,
            pricingType: 'custom',
            summary: {
              price: offer.price,
              quantity,
              subtotal,
              deliveryFee,
              serviceFee,
              tax,
              total,
              depositAmount,
              remainingAmount: offer.orderType === 'deposit' ? total - depositAmount : undefined,
            },
            amountToPay,
            orderType: offer.orderType,
            depositPercentage: offer.depositPercentage,
            remainingBalanceDueDate: offer.remainingBalanceDueDate,
            refundPolicy: offer.orderType === 'deposit' ? (offer.refundPolicy || 'refundable') : undefined,
            addedServices: offer.addedServices || [],
            addedServicesTotal,
            buyerRequirements: (offer.buyerRequirements || []).map((q) => ({
              question: q.question,
              image: q.image || '',
              answer: '',
            })),
          };

          setOrderItems([orderItem]);
          setUpdatedAggregateSummary({
            subtotal,
            deliveryFee,
            serviceFee,
            tax,
            total: amountToPay,
          });
          setRequirementAnswers((offer.buyerRequirements || []).map((q) => ({
            question: q.question,
            image: q.image || '',
            answer: '',
          })));
        })
        .catch((error) => {
          console.error(error);
          showToast('Failed to fetch chat message', 'error');
        })
        .finally(() => setPlaceOrderLoading(false));
    } else {
      setOrderItems(
        initialOrderItems.map((item) => ({
          ...item,
          isB2B: Boolean(item.isB2B),
          orderType: 'single',
          amountToPay: item.summary.total,
        }))
      );

      if (initialOrderItems.length > 0) {
        const firstProductId = initialOrderItems[0].productId;
        if (initialOrderItems[0].isB2B) {
          axios
            .get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/b2b/listings/${firstProductId}`)
            .then((res) => {
              const listing = res.data.listing;
              const leadDays = (Number(listing?.productionTimeDays) || 0) + (Number(listing?.shippingTimeDays) || 0);
              const timeStr = `${Math.max(1, leadDays)} Days`;
              const minDate = getMinSelectableDate(timeStr);
              setCheckoutMeta({ minTimeStr: timeStr, deliveryType: initialOrderItems[0].deliveryType || 'delivery' });
              setMinSelectableDate(minDate || '');
              setIsServiceOrder(listing?.listingType === 'service');
              setServiceMode('');
              setUnavailableDates([]);
              setSellerUserId(listing?.b2bSellerId?.userId || null);
              setDerivedPickupInfo(null);
              setDerivedSellerLocation(null);
            })
            .catch(() => {
              setMinSelectableDate(getMinSelectableDate('1 Days') || '');
            });
          return;
        }

        axios
          .get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/products/product/${firstProductId}`)
          .then((res) => {
            const p = res.data.product;
            const isService = p.listingType === 'service';
            const sMode = isService ? (p.serviceMode || '') : '';
            setIsServiceOrder(isService);
            setServiceMode(sMode);
            setSellingScope(p?.sellingScope || 'local');
            const deliveryType = initialOrderItems[0].deliveryType;
            const isFixed = p.pricingType === 'fixed';
            const pkgIndex = initialOrderItems[0]?.selectedOptions?.fixedPricing?.packageIndex ?? 0;
            const opts = isFixed ? p.packages?.[pkgIndex]?.options : p.variablePricing;
            const timeStr = opts?.[deliveryType === 'delivery' ? 'timeToDeliver' : 'timeframe'] || opts?.timeToDeliver || '1 Days';

            const minDate = getMinSelectableDate(timeStr);
            setCheckoutMeta({ minTimeStr: timeStr, deliveryType });
            setMinSelectableDate(minDate || '');

            if (deliveryType === 'pickup' && p.pickupLocation) {
              setDerivedPickupInfo({
                address: p.pickupLocation.address || '',
                city: p.pickupLocation.city || '',
                state: p.pickupLocation.state?.name || '',
                country: p.pickupLocation.country?.name || '',
                zipCode: p.pickupLocation.zipCode || '',
              });
            }
            setDerivedSellerLocation(listingPoint(p) || null);

            const sellerId = p.sellerId?._id || p.sellerId;
            if (sellerId) {
              setSellerUserId(p.sellerId?.userId?._id || p.sellerId?.userId);
              axios
                .get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/sellers/seller/${sellerId}/unavailable-dates`)
                .then((r) => setUnavailableDates(r.data.unavailableDates || []))
                .catch(() => setUnavailableDates([]));
            }
          })
          .catch(() => {
            setMinSelectableDate(getMinSelectableDate('1 Days') || '');
          });
      }
    }
  }, [messageId, taxRate, feesObj?.loaded]);

  const calculateTaxRate = () => {
    if (!formData.country) return 0;
    if (formData.country.label !== 'Canada') return 5;
    if (!formData.state) return 0;
    const stateData = states.find((s) => s.name === formData.state.label);
    return stateData ? stateData.tax : 0;
  };

  useEffect(() => {
    const newTaxRate = calculateTaxRate();
    setTaxRate(newTaxRate);

    // Local estimate, shown immediately so the summary is never blank while the
    // quote is in flight. The server's figures replace it below.
    const subtotal = orderItems.reduce((acc, item) => acc + item.summary.subtotal, 0);
    const deliveryFee = orderItems.reduce((acc, item) => acc + item.summary.deliveryFee, 0);
    const serviceFee = orderItems.reduce((acc, item) => acc + item.summary.serviceFee, 0);
    const tax = (subtotal + deliveryFee + serviceFee) * (newTaxRate / 100);
    const amountToPay = orderItems.reduce((acc, item) => acc + item.amountToPay, 0);

    setUpdatedAggregateSummary({
      subtotal,
      deliveryFee,
      serviceFee,
      tax,
      total: amountToPay,
    });
  }, [formData.country, formData.state, orderItems]);

  /**
   * Replace the local estimate with the server's pricing.
   *
   * The server is the authority on what the card will be charged — it prices
   * from the listing and the seller's shipping policy rather than from figures
   * carried in the basket. Showing anything else risks quoting one number and
   * charging another.
   */
  useEffect(() => {
    const authToken = localStorage.getItem('token');
    if (!authToken || orderItems.length === 0) return;

    let cancelled = false;

    axios
      .post(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/quote`,
        { orderItems },
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then(({ data }) => {
        if (cancelled || !data?.aggregate) return;
        setUpdatedAggregateSummary(data.aggregate);
        setQuotedMinTimeStr(data.dateRequirement?.label || '');
      })
      .catch(() => {
        // Leave the local estimate in place; createPaymentIntent re-prices and
        // will surface any real problem before the buyer is charged.
      });

    return () => { cancelled = true; };
  }, [orderItems]);

  const dateErrorFor = (date) => {
    if (earliestDate && date < earliestDate) {
      return {
        type: 'minTime',
        message: `Minimum ${minTimeLabel || 'delivery/pickup time'} required. Please select a date on or after ${new Date(earliestDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}.`,
      };
    }
    if (unavailableDates.includes(date)) {
      return { type: 'unavailable', message: 'Seller is unavailable on this date.' };
    }
    return null;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'requestedDate') setDateValidationError(value ? dateErrorFor(value) : null);
    setFormErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleDropdownChange = (field, selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      [field]: selectedOption,
      ...(field === 'country' && { state: null }),
    }));
    setFormErrors((prev) => {
      const next = { ...prev };
      if (next[field]) delete next[field];
      if (field === 'country' && next.state) delete next.state;
      return next;
    });
  };

  const scrollToFirstCheckoutError = (newErrors) => {
    const fieldOrder = ['requestedDate', 'requirementAnswers', 'country', 'state', 'city', 'address', 'postalCode', 'fullName', 'email', 'phone'];
    const firstKey = fieldOrder.find((key) => newErrors[key]);
    if (!firstKey) return;

    window.requestAnimationFrame(() => {
      const target = document.querySelector(`[data-checkout-field="${firstKey}"]`);
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });

      const input = target.matches('input, textarea, select, button')
        ? target
        : target.querySelector('input, textarea, select, button');
      input?.focus?.({ preventScroll: true });
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.country) newErrors.country = 'Country is required';
    if (!formData.state) newErrors.state = 'State / Province is required';
    if (!formData.city?.trim()) newErrors.city = 'City is required';
    if (!formData.address?.trim()) newErrors.address = 'Complete address is required';
    if (!formData.postalCode?.trim()) newErrors.postalCode = 'Postal / ZIP code is required';
    if (!formData.fullName?.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email?.trim()) newErrors.email = 'Email is required';
    if (!formData.phone?.trim()) newErrors.phone = 'Phone is required';

    const skipDateValidation = (isServiceOrder && serviceMode === 'online') || sellingScope === 'international';
    if (!skipDateValidation && !formData.requestedDate) {
      newErrors.requestedDate = 'Please select your preferred delivery/pickup date';
    }
    if (messageId && requirementAnswers.some((req) => !req.answer?.trim())) {
      newErrors.requirementAnswers = 'Please answer all required seller questions';
    }

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      scrollToFirstCheckoutError(newErrors);
      showToast('Please fill in all required fields', 'error');
      return false;
    }

    setFormErrors({});

    if (!skipDateValidation) {
      if (formData.requestedDate < todayStr) {
        const dateErrors = { requestedDate: 'Please select today or a future date' };
        setFormErrors(dateErrors);
        scrollToFirstCheckoutError(dateErrors);
        showToast('Please select today or a future date', 'error');
        return false;
      }
      const dateError = dateErrorFor(formData.requestedDate);
      if (dateError) {
        setDateValidationError(dateError);
        scrollToFirstCheckoutError({ requestedDate: true });
        showToast(dateError.message, 'error');
        return false;
      }
    }
    setDateValidationError(null);
    return true;
  };

  // The order already succeeded, so a failure here is only reported, never thrown.
  const saveDeliveryAddressToAccount = async (token) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/auth/updateSettings`,
        {
          section: 'deliveryAddress',
          data: {
            address: {
              country: { name: formData.country.label, code: formData.country.value },
              state: { name: formData.state.label, tax: taxRate },
              city: formData.city,
              address: formData.address,
              zipCode: formData.postalCode,
            },
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchUserData();
    } catch {
      showToast('Your order was placed, but the address could not be saved to your account', 'error');
    }
  };

  const handleConfirmOrder = async () => {
    if (!validateForm()) return;
    if (totalToPay < 5) {
      showToast('Minimum order amount must be USD 5', 'error');
      return;
    }
    if (cardAmountDue > 0) {
      if (!stripe || !elements) {
        showToast('Stripe is not loaded', 'error');
        return;
      }
      const cardElement = elements.getElement(CardNumberElement);
      if (!cardElement) {
        showToast('Please enter card details', 'error');
        return;
      }
    }

    setPlaceOrderLoading(true);
    try {
      const token = localStorage.getItem('token');

      const orderItemsToSend = orderItems.map((item) => ({
        productId: item.productId,
        isB2B: Boolean(item.isB2B),
        quantity: item.quantity,
        selectedOptions: item.selectedOptions,
        price: item.price,
        deliveryType: item.deliveryType,
        timeToDeliveryOrPickup: item.timeToDeliveryOrPickup,
        pricingType: item.pricingType,
        name: item.name,
        thumbnail: item.thumbnail,
        summary: item.summary,
        amountToPay: item.amountToPay,
        orderType: item.orderType,
        depositPercentage: item.depositPercentage,
        remainingBalanceDueDate: item.remainingBalanceDueDate,
        refundPolicy: item.refundPolicy,
        addedServices: item.addedServices || [],
        addedServicesTotal: item.addedServicesTotal || 0,
        buyerRequirements: messageId ? requirementAnswers : (item.buyerRequirements || []),
      }));

      let paymentIntentId = null;
      const requestedDeliveryDate = formData.requestedDate ? new Date(formData.requestedDate + 'T12:00:00') : undefined;
      const intentResponse = await axios.post(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/create-payment-intent`,
        { orderItems: orderItemsToSend, creditAmount: creditToApply, requestedDeliveryDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { clientSecret, fullCredits: apiFullCredits, appliedCreditAmount } = intentResponse.data;
      const appliedCredits = typeof appliedCreditAmount === 'number' ? appliedCreditAmount : creditToApply;
      const remainingCard = Math.max(0, totalToPay - appliedCredits);

      if (!apiFullCredits && remainingCard > 0) {
        const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: elements.getElement(CardNumberElement),
        });
        if (pmError) {
          showToast(pmError.message, 'error');
          setPlaceOrderLoading(false);
          return;
        }
        const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: paymentMethod.id,
        });
        if (error) {
          showToast(error.message, 'error');
          setPlaceOrderLoading(false);
          return;
        }
        paymentIntentId = paymentIntent?.status === 'succeeded' ? paymentIntent.id : null;
      }

      const orderData = {
        messageId,
        paymentIntentId: paymentIntentId || undefined,
        fullCredits: !!(apiFullCredits || remainingCard <= 0),
        creditAmount: appliedCredits,
        orderItems: orderItemsToSend,
        requestedDeliveryDate,
        deliveryDetails: {
          country: { name: formData.country.label, code: formData.country.value },
          state: { name: formData.state.label, tax: taxRate },
          city: formData.city,
          address: formData.address,
          postalCode: formData.postalCode,
          unitNumber: formData.unitNumber || '',
          note: formData.note,
        },
        personalDetails: {
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
        },
      };

      const confirmResponse = await axios.post(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/confirm`, orderData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (confirmResponse.data.success) {
        showToast('Order placed successfully', 'success');
        if (saveAsDefaultAddress) await saveDeliveryAddressToAccount(token);
        if (!messageId && (navigationFromCart || cartSynced)) {
          const purchasedLineIds = orderItems.map((o) => o.cartItemId).filter(Boolean);
          if (purchasedLineIds.length > 0) {
            await removeCartLinesByIds(purchasedLineIds);
          } else {
            await clearCart();
          }
        }
        navigate('/orders');
      } else {
        showToast('Failed to place order', 'error');
      }
    } catch (error) {
      showToast(error.response?.data?.error || 'An error occurred', 'error');
    } finally {
      setPlaceOrderLoading(false);
    }
  };

  const hasNoItems = (!orderItems || orderItems.length === 0) && !messageId;
  if (hasNoItems) {
    if (checkoutCartLoading) {
      return (
        <div className="checkoutPageRedesign" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
          <Loader type="simple" />
        </div>
      );
    }
    return (
      <div className="checkoutPageRedesign checkoutEmptyCartSection">
        <div className="checkoutEmptyCartCard">
          <div className="checkoutEmptyCartIconWrap">
            <FaShoppingCart className="checkoutEmptyCartIcon" />
          </div>
          <h1 className="checkoutEmptyCartTitle">Your cart is empty</h1>
          <p className="checkoutEmptyCartSubtitle">Add items from products or services to proceed to checkout.</p>
          <div className="checkoutEmptyCartActions">
            <button type="button" className="primaryBtn checkoutEmptyCartBtn" onClick={() => navigate('/products-services')}>
              Browse products & services
            </button>
            <button type="button" className="modalButton cancel checkoutEmptyCartBtnSecondary" onClick={() => navigate('/')}>
              Back to home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const deliveryType = orderItems[0]?.deliveryType || 'delivery';
  const isDelivery = deliveryType === 'delivery';
  const addressHeading = isServiceOrder
    ? (serviceMode === 'onsite' ? 'Your Address (Service Location)' : 'Your Address (For Records)')
    : isDelivery ? 'Delivery Address' : 'Address (Required for order details)';

  return (
    <div className="checkoutPageRedesign">
      {placeOrderLoading && <Loader type="checkmate" />}

      <div className="checkoutPageHeader">
        <h1 className="checkoutPageTitle">Secure Checkout</h1>
        <p className="checkoutPageSubtitle">Complete your order in a few simple steps</p>
      </div>

      <div className="checkoutPageGrid">
        <div className="checkoutMainContent">
          {/* Section 1: Delivery / Pickup Date */}
          {!(isServiceOrder && serviceMode === 'online') && sellingScope !== 'international' && (
            <div className="checkoutSectionCard">
              <div className="checkoutSectionHeader">
                <span className="checkoutSectionIcon"><FaCalendarAlt /></span>
                <div>
                  <h2 className="checkoutSectionTitle">
                    {isServiceOrder ? 'Preferred Service Date' : isDelivery ? 'Preferred Delivery Date' : 'Preferred Pickup Date'}
                  </h2>
                  <p className="checkoutSectionHint">
                    {isServiceOrder
                      ? 'Select your preferred date for the service.'
                      : `Select when you need your order ready. Minimum: ${minTimeLabel || '—'}`}
                  </p>
                </div>
              </div>
              <div className="checkoutSectionBody">
                <div className="checkoutDateField">
                  <div data-checkout-field="requestedDate">
                  <label className="checkoutFormLabel">Select date *</label>
                  <input
                    type="date"
                    className={`inputField checkoutDateInput ${dateValidationError || formErrors.requestedDate ? 'inputError' : ''}`}
                    value={formData.requestedDate}
                    min={earliestDate || todayStr}
                    onChange={(e) => handleInputChange('requestedDate', e.target.value)}
                    title="Select when you need your order"
                  />
                  {formErrors.requestedDate && <div className="checkoutFieldError">{formErrors.requestedDate}</div>}
                  {dateValidationError && (
                    <div className="checkoutDateError">
                      <span>{dateValidationError.message}</span>
                      {sellerUserId && (
                        <button type="button" className="checkoutContactSellerBtn" onClick={() => openFloatingChat(sellerUserId)}>
                          <FaComment /> Contact Seller
                        </button>
                      )}
                    </div>
                  )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Section 2: Address */}
          <div className="checkoutSectionCard">
            <div className="checkoutSectionHeader">
              <span className="checkoutSectionIcon">{isServiceOrder ? <FaMapMarkerAlt /> : isDelivery ? <FaTruck /> : <FaStore />}</span>
              <div>
                <h2 className="checkoutSectionTitle">{addressHeading}</h2>
                <p className="checkoutSectionHint">
                  {isServiceOrder && serviceMode === 'onsite'
                    ? 'Where should the service be performed?'
                    : isServiceOrder
                      ? 'Required for order records and communication.'
                      : isDelivery ? 'Where should we deliver your order?' : 'Required for order records and communication.'}
                </p>
              </div>
            </div>
            <div className="checkoutSectionBody">
              {messageId && requirementAnswers.length > 0 && (
                <div className="checkoutFormGrid" style={{ marginBottom: '16px' }}>
                  <div className="checkoutFormGroup checkoutFormFull" data-checkout-field="requirementAnswers">
                    <label className="checkoutFormLabel">Answer Seller Questions *</label>
                    {requirementAnswers.map((req, idx) => (
                      <div key={`${req.question}-${idx}`} style={{ marginBottom: '10px' }}>
                        <label className="checkoutFormLabel" style={{ fontWeight: 500 }}>{req.question}</label>
                        {req.image && (
                          <div style={{ marginBottom: '6px' }}>
                            <img src={req.image} alt="Requirement" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }} />
                          </div>
                        )}
                        <input
                          type="text"
                          className={`inputField ${formErrors.requirementAnswers && !req.answer?.trim() ? 'checkoutInputError' : ''}`}
                          placeholder="Type your answer"
                          value={req.answer}
                          onChange={(e) => {
                            const next = [...requirementAnswers];
                            next[idx].answer = e.target.value;
                            setRequirementAnswers(next);
                            if (e.target.value.trim()) {
                              setFormErrors((prev) => {
                                if (!prev.requirementAnswers) return prev;
                                const updated = { ...prev };
                                delete updated.requirementAnswers;
                                return updated;
                              });
                            }
                          }}
                        />
                      </div>
                    ))}
                    {formErrors.requirementAnswers && <div className="checkoutFieldError">{formErrors.requirementAnswers}</div>}
                  </div>
                </div>
              )}
              {orderItems[0]?.deliveryType === 'pickup' && effectivePickupInfo && (
                <div className="checkoutPickupInfoCard">
                  <h3 className="checkoutPickupTitle"><FaMapMarkerAlt /> Pickup Location</h3>
                  <div className="checkoutPickupDetails">
                    <div className="checkoutPickupRow"><span>City</span><span>{effectivePickupInfo.city}</span></div>
                    <div className="checkoutPickupRow"><span>State</span><span>{effectivePickupInfo.state}</span></div>
                    <div className="checkoutPickupRow"><span>Country</span><span>{effectivePickupInfo.country}</span></div>
                    {kmAway !== null && <div className="checkoutPickupRow checkoutPickupKm"><span>Distance</span><span>{kmAway} KM away</span></div>}
                    <p className="checkoutPickupNote">The exact pickup address is shared on your order page after payment.</p>
                  </div>
                </div>
              )}
              <div className="checkoutFormGrid">
                <div className="checkoutFormGroup" data-checkout-field="country">
                  <label className="checkoutFormLabel">Country *</label>
                  <Dropdown
                    options={countries.map((c) => ({ value: c.code, label: c.name }))}
                    value={formData.country}
                    onChange={(s) => handleDropdownChange('country', s)}
                    placeholder="Select Country"
                  />
                  {formErrors.country && <div className="checkoutFieldError">{formErrors.country}</div>}
                </div>
                <div className="checkoutFormGroup" data-checkout-field="state">
                  <label className="checkoutFormLabel">State / Province *</label>
                  {formData.country?.label === 'Canada' ? (
                    <Dropdown
                      options={states.map((s) => ({ value: s.name, label: s.name }))}
                      value={formData.state}
                      onChange={(s) => handleDropdownChange('state', s)}
                      placeholder="Select State"
                    />
                  ) : (
                    <input
                      type="text"
                      className={`inputField ${formErrors.state ? 'checkoutInputError' : ''}`}
                      placeholder="State / Province"
                      value={formData.state?.label || ''}
                      onChange={(e) => handleDropdownChange('state', { value: e.target.value, label: e.target.value })}
                    />
                  )}
                  {formErrors.state && <div className="checkoutFieldError">{formErrors.state}</div>}
                </div>
                <div className="checkoutFormGroup checkoutFormFull" data-checkout-field="city">
                  <label className="checkoutFormLabel">City *</label>
                  <input
                    type="text"
                    className={`inputField ${formErrors.city ? 'checkoutInputError' : ''}`}
                    placeholder="City"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                  />
                  {formErrors.city && <div className="checkoutFieldError">{formErrors.city}</div>}
                </div>
                <div className="checkoutFormGroup checkoutFormFull" data-checkout-field="address">
                  <label className="checkoutFormLabel">Complete Address *</label>
                  <input
                    type="text"
                    className={`inputField ${formErrors.address ? 'checkoutInputError' : ''}`}
                    placeholder="Street address, apartment, suite, etc."
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                  />
                  {formErrors.address && <div className="checkoutFieldError">{formErrors.address}</div>}
                </div>
                <div className="checkoutFormGroup" data-checkout-field="postalCode">
                  <label className="checkoutFormLabel">Postal / ZIP Code *</label>
                  <input
                    type="text"
                    className={`inputField ${formErrors.postalCode ? 'checkoutInputError' : ''}`}
                    placeholder="e.g. M5V 2T6 or 10001"
                    value={formData.postalCode}
                    onChange={(e) => handleInputChange('postalCode', e.target.value)}
                  />
                  {formErrors.postalCode && <div className="checkoutFieldError">{formErrors.postalCode}</div>}
                </div>
                <div className="checkoutFormGroup">
                  <label className="checkoutFormLabel">Unit / Apt # (optional)</label>
                  <input
                    type="text"
                    className="inputField"
                    placeholder="e.g. Apt 4B, Suite 200"
                    value={formData.unitNumber}
                    onChange={(e) => handleInputChange('unitNumber', e.target.value)}
                  />
                </div>
                <div className="checkoutFormGroup checkoutFormFull">
                  <label className="checkoutFormLabel">Note for Seller (optional)</label>
                  <textarea
                    className="inputField checkoutTextarea"
                    placeholder="Delivery instructions, special requests..."
                    value={formData.note}
                    onChange={(e) => handleInputChange('note', e.target.value)}
                    rows={3}
                  />
                </div>
                <label className="checkoutFormGroup checkoutFormFull checkoutSaveAddress">
                  <input
                    type="checkbox"
                    checked={saveAsDefaultAddress}
                    onChange={(e) => setSaveAsDefaultAddress(e.target.checked)}
                  />
                  Save as my default delivery address
                </label>
              </div>
            </div>
          </div>

          {/* Section 3: Personal Details */}
          <div className="checkoutSectionCard">
            <div className="checkoutSectionHeader">
              <span className="checkoutSectionIcon"><FaUser /></span>
              <h2 className="checkoutSectionTitle">Contact Information</h2>
            </div>
            <div className="checkoutSectionBody">
              <div className="checkoutFormGrid checkoutFormGridThree">
                <div className="checkoutFormGroup" data-checkout-field="fullName">
                  <label className="checkoutFormLabel">Full Name *</label>
                  <input
                    type="text"
                    className={`inputField ${formErrors.fullName ? 'checkoutInputError' : ''}`}
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                  />
                  {formErrors.fullName && <div className="checkoutFieldError">{formErrors.fullName}</div>}
                </div>
                <div className="checkoutFormGroup" data-checkout-field="email">
                  <label className="checkoutFormLabel">Email *</label>
                  <input
                    type="email"
                    className={`inputField ${formErrors.email ? 'checkoutInputError' : ''}`}
                    placeholder="Email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                  {formErrors.email && <div className="checkoutFieldError">{formErrors.email}</div>}
                </div>
                <div className="checkoutFormGroup" data-checkout-field="phone">
                  <label className="checkoutFormLabel">Phone *</label>
                  <input
                    type="tel"
                    className={`inputField ${formErrors.phone ? 'checkoutInputError' : ''}`}
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                  />
                  {formErrors.phone && <div className="checkoutFieldError">{formErrors.phone}</div>}
                </div>
              </div>
            </div>
          </div>

          {/* Section 4: Payment */}
          <div className="checkoutSectionCard">
            <div className="checkoutSectionHeader">
              <span className="checkoutSectionIcon"><FaCreditCard /></span>
              <h2 className="checkoutSectionTitle">Payment Method</h2>
            </div>
            <div className="checkoutSectionBody">
              {balanceNum > 0 && (
                <label className="checkoutCreditsApplyRow">
                  <input
                    type="checkbox"
                    checked={applyPlatformCredits}
                    onChange={(e) => setApplyPlatformCredits(e.target.checked)}
                  />
                  <span>
                    Apply account credits ({formatCurrencyDual(balanceNum, currency, exchangeRate)} available)
                    {applyPlatformCredits && creditToApply > 0 && (
                      <span className="checkoutCreditsApplyHint">
                        {' '}— {formatCurrencyDual(creditToApply, currency, exchangeRate)} will be used toward this order
                      </span>
                    )}
                  </span>
                </label>
              )}
              {applyPlatformCredits && creditToApply > 0 && cardAmountDue > 0 && (
                <p className="checkoutCreditsSplitNote">
                  The rest ({formatCurrencyDual(cardAmountDue, currency, exchangeRate)}) will be charged to your card.
                </p>
              )}
              {applyPlatformCredits && paysWithCreditsOnly && (
                <p className="checkoutCreditsOnlyNote">
                  Your order total will be covered from account credits. No card charge.
                </p>
              )}
              {cardAmountDue > 0 && (
                <div className="checkoutStripeFields">
                  <div className="checkoutStripeRow">
                    <label>Card Number</label>
                    <CardNumberElement className="inputField checkoutStripeElement" options={{ style: { base: { fontSize: '16px' } } }} />
                  </div>
                  <div className="checkoutStripeRowGroup">
                    <div className="checkoutStripeRow">
                      <label>Expiry</label>
                      <CardExpiryElement className="inputField checkoutStripeElement" options={{ style: { base: { fontSize: '16px' } } }} />
                    </div>
                    <div className="checkoutStripeRow">
                      <label>CVC</label>
                      <CardCvcElement className="inputField checkoutStripeElement" options={{ style: { base: { fontSize: '16px' } } }} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Order Summary Sidebar */}
        <aside className="checkoutSidebar">
          <div className="checkoutOrderCard">
            <h3 className="checkoutOrderTitle">Order Summary</h3>
            <div className="checkoutOrderItems">
              {orderItems.map((item, i) => (
                <div key={i} className="checkoutOrderItem">
                  <div className="checkoutOrderItemImg">
                    <img src={item.thumbnail} alt={item.name} />
                    <span className="checkoutOrderItemQty">{item.quantity}</span>
                  </div>
                  <div className="checkoutOrderItemInfo">
                    <span className="checkoutOrderItemName">{item.name}</span>
                    <span className="checkoutOrderItemPrice">{formatCurrencyDual(item.price, currency, exchangeRate)}</span>
                    {item.addedServices && item.addedServices.length > 0 && (
                      <div style={{ marginTop: '4px', fontSize: '11px', color: '#6b7280' }}>
                        {item.addedServices.map((svc, idx) => (
                          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                            <span>+ {svc.name} (x{svc.qty || 1})</span>
                            <span>{formatCurrencyDual((parseFloat(svc.price) || 0) * (svc.qty || 1) * (svc.pricingMode === 'perUnit' ? item.quantity : 1), currency, exchangeRate)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="checkoutOrderTotals">
              <div className="checkoutOrderTotalRow"><span>Subtotal</span><span>{formatCurrencyDual(updatedAggregateSummary?.subtotal, currency, exchangeRate)}</span></div>
              <div className="checkoutOrderTotalRow"><span>Delivery Fee</span><span>{formatCurrencyDual(updatedAggregateSummary?.deliveryFee, currency, exchangeRate)}</span></div>
              <div className="checkoutOrderTotalRow"><span>Service Fee</span><span>{formatCurrencyDual(updatedAggregateSummary?.serviceFee, currency, exchangeRate)}</span></div>
              <div className="checkoutOrderTotalRow"><span>Tax</span><span>{formatCurrencyDual(updatedAggregateSummary?.tax, currency, exchangeRate)}</span></div>
              {orderItems[0]?.orderType === 'deposit' && (
                <>
                  <div className="checkoutOrderTotalRow"><span>Total</span><span>{formatCurrencyDual(orderItems[0]?.summary?.total, currency, exchangeRate)}</span></div>
                  <div className="checkoutOrderTotalRow checkoutOrderTotalFinal"><span>Deposit</span><span>{formatCurrencyDual(orderItems[0]?.amountToPay, currency, exchangeRate)}</span></div>
                </>
              )}
              {orderItems[0]?.orderType !== 'deposit' && (
                <div className="checkoutOrderTotalRow checkoutOrderTotalFinal">
                  <span>Total</span><span>{formatCurrencyDual(updatedAggregateSummary?.total, currency, exchangeRate)}</span>
                </div>
              )}
            </div>
            {creditToApply > 0 && (
              <div className="checkoutOrderTotalRow checkoutOrderCreditRow">
                <span>Account credits</span>
                <span>-{formatCurrencyDual(creditToApply, currency, exchangeRate)}</span>
              </div>
            )}
            {cardAmountDue > 0 && (
              <div className="checkoutOrderTotalRow checkoutOrderTotalFinal">
                <span>Charge to card</span>
                <span>{formatCurrencyDual(cardAmountDue, currency, exchangeRate)}</span>
              </div>
            )}
            <p className="checkoutFeesNote">* Service fee is non-refundable</p>
            <button
              className="checkoutPayBtn"
              onClick={handleConfirmOrder}
              disabled={placeOrderLoading}
            >
              {cardAmountDue <= 0 && totalToPay > 0
                ? 'Place order with credits'
                : cardAmountDue > 0
                  ? `Pay ${formatCurrencyDualText(cardAmountDue, currency, exchangeRate)}`
                  : `Pay ${formatCurrencyDualText(totalToPay, currency, exchangeRate)}`}
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default CheckoutPage;
